import type { QuoteInfo } from 'apiConnector/scheduledRun/model';
import classNames from 'classnames';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import { ConnectedScheduleModal } from 'components/shared/ScheduledRun/Modal/ScheduleModal';
import type { IdOwnProps } from 'components/shared/ScheduledRun/type';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { connect, type MapStateToProps, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { addToScheduleThunk } from 'state/scheduledRuns/thunks';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { hasUserPermission } from 'state/user';

interface StateProps {
  quotes?: QuoteInfo[];
  isVisible?: boolean;

  disabled?: boolean;
}

interface DispatchProps {
  onOpenAddToScheduleRun: (quotes: QuoteInfo[]) => void;
  onOpenNewScheduleRun: (quotes: QuoteInfo[]) => void;
}
export interface ScheduleMenuAnalyticProps {
  analyticContext: 'batch' | 'static' | 'dynamic' | 'quotes blotter' | 'trades blotter';
}
export interface ScheduleMenuOwnProps extends IdOwnProps, ScheduleMenuAnalyticProps {
  btnClassName?: string;
  wrapperClassName?: string;
  isButtonLabelVisible?: boolean;
}
interface Props extends StateProps, DispatchProps, ScheduleMenuOwnProps {}
export function connectScheduleMenu<T extends ScheduleMenuOwnProps>(
  mapStateToProps: MapStateToProps<StateProps, T, AppState>,
) {
  const mapDispatchToProps = (dispatch: ThunkDispatch, { id }: T): DispatchProps => ({
    onOpenAddToScheduleRun(quotes) {
      dispatch(addToScheduleThunk(id, quotes));
    },
    onOpenNewScheduleRun(quotes) {
      dispatch(scheduledRunActions.editSchedule(id, quotes));
    },
  });
  return connect(mapStateToProps, mapDispatchToProps)(ScheduleMenu);
}

const primaryMarketRights = ['PRIMARY_MARKET_QUOTE_ONLY', 'PRIMARY_MARKET_TRADE'] satisfies readonly Permission[];

function hasScheduledRunRights(state: AppState) {
  return primaryMarketRights.some(right => hasUserPermission(state, right));
}

export function ScheduleMenu({
  quotes = [],
  isVisible,
  onOpenAddToScheduleRun,
  onOpenNewScheduleRun,
  id,
  btnClassName,
  wrapperClassName,
  analyticContext,
  disabled,
  isButtonLabelVisible,
}: Props) {
  const isFeatureActive = useToggleFeature('scheduledRunsParis');
  const hasScheduledRunRight = useSelector(hasScheduledRunRights);
  const isScheduledRunEnabled = isFeatureActive && hasScheduledRunRight && isVisible;
  return (
    isScheduledRunEnabled && (
      <>
        <Dropdown className={wrapperClassName}>
          <SpDropdownToggle
            caret={false}
            variant="link"
            data-e2e="scheduled-run-open-menu"
            disabled={disabled || quotes.length === 0}
            className={classNames(btnClassName, 'btn btn-lg btn-flat-primary btn-icon-end p-2')}
          >
            {isButtonLabelVisible && (
              <span className="d-none d-lg-block">
                <FormattedMessage id="scheduledRun.schedule" />
              </span>
            )}
            <i className="icon">more_time</i>
          </SpDropdownToggle>
          <DropdownMenu>
            <DropdownItem
              onClick={() => {
                if (analyticContext) {
                  logAnalytics.trackEvent('Scheduled run', `Create from ${analyticContext}`);
                }
                onOpenNewScheduleRun(quotes);
              }}
            >
              <FormattedMessage id="scheduledRun.addToNewSchedule.button" />
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                if (analyticContext) {
                  logAnalytics.trackEvent('Scheduled run', `Amend from ${analyticContext}`);
                }
                onOpenAddToScheduleRun(quotes);
              }}
            >
              <FormattedMessage id="scheduledRun.updateQuotes" />
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <ConnectedScheduleModal id={id} />
      </>
    )
  );
}
